import * as THREE from "three";

const VOLT = 0xffe600;

function jaggedPath(start, end, detail = 5, roughness = 0.3) {
    let pts = [start.clone(), end.clone()];
    let offset = start.distanceTo(end) * roughness;
    for (let d = 0; d < detail; d++) {
        const next = [];
        for (let i = 0; i < pts.length - 1; i++) {
            const a = pts[i];
            const b = pts[i + 1];
            const mid = a.clone().add(b).multiplyScalar(0.5);
            mid.x += (Math.random() - 0.5) * offset;
            mid.y += (Math.random() - 0.5) * offset;
            mid.z += (Math.random() - 0.5) * offset;
            next.push(a, mid);
        }
        next.push(pts[pts.length - 1]);
        pts = next;
        offset *= 0.55;
    }
    return pts;
}

function tubeAlong(pts, radius, color, opacity) {
    const curve = new THREE.CatmullRomCurve3(pts, false, "catmullrom", 0.2);
    const segs = Math.min(220, Math.max(24, pts.length * 4));
    const geo = new THREE.TubeGeometry(curve, segs, radius, 5, false);
    const mat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
        toneMapped: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.userData.baseOpacity = opacity;
    geo.setDrawRange(0, 0);
    return { mesh, total: geo.index.count, geo, mat };
}

function glowTexture() {
    const c = document.createElement("canvas");
    c.width = c.height = 128;
    const g = c.getContext("2d");
    const grad = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    grad.addColorStop(0, "rgba(255,255,255,1)");
    grad.addColorStop(0.25, "rgba(255,230,0,0.85)");
    grad.addColorStop(1, "rgba(255,230,0,0)");
    g.fillStyle = grad;
    g.fillRect(0, 0, 128, 128);
    return new THREE.CanvasTexture(c);
}

export class SpdaScene {
    constructor(canvas, opts = {}) {
        this.canvas = canvas;
        this.onStrike = opts.onStrike || (() => {});
        this.autoEnabled = opts.auto !== false;
        this.kv = 400;
        this.shieldOn = true;
        this.inView = false;
        this.bolts = [];
        this.autoTimer = 2.8;
        this.pointer = { x: 0, y: 0, tx: 0, ty: 0 };
        this.flashI = 0;
        this.domeT = 1;
        this.disposed = false;

        this.renderer = new THREE.WebGLRenderer({
            canvas,
            antialias: true,
            alpha: true,
        });
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1.75));
        this.renderer.toneMapping = THREE.NoToneMapping;

        this.scene = new THREE.Scene();
        this.scene.fog = new THREE.FogExp2(0x0a0d14, 0.042);

        this.camera = new THREE.PerspectiveCamera(36, 16 / 9, 0.1, 140);
        this.camBase = new THREE.Vector3(9.8, 6.2, 12);
        this.camera.position.copy(this.camBase);

        this.dir = new THREE.DirectionalLight(0x8ea6d8, 1.6);
        this.dir.position.set(6, 12, 5);
        this.scene.add(this.dir);
        this.scene.add(new THREE.AmbientLight(0x28324a, 2.2));

        this.flash = new THREE.PointLight(VOLT, 0, 34, 1.1);
        this.scene.add(this.flash);

        this.buildCity();
        this.buildDome();
        this.buildStars();
        this.glowTex = glowTexture();

        this._onPointer = (e) => {
            const r = canvas.getBoundingClientRect();
            this.pointer.tx = ((e.clientX - r.left) / r.width - 0.5) * 2;
            this.pointer.ty = ((e.clientY - r.top) / r.height - 0.5) * 2;
        };
        canvas.parentElement.addEventListener("pointermove", this._onPointer);

        this.clock = new THREE.Clock();
        this._loop = this.loop.bind(this);
        this._loop();
    }

    buildCity() {
        const g = new THREE.Group();
        const faceMat = new THREE.MeshStandardMaterial({
            color: 0x101828,
            roughness: 0.85,
            metalness: 0.2,
        });
        const edgeMat = new THREE.LineBasicMaterial({
            color: 0x4a5f8f,
            transparent: true,
            opacity: 0.9,
        });

        const volume = (w, h, d, x, y, z) => {
            const geo = new THREE.BoxGeometry(w, h, d);
            const mesh = new THREE.Mesh(geo, faceMat);
            mesh.position.set(x, y + h / 2, z);
            const edges = new THREE.LineSegments(
                new THREE.EdgesGeometry(geo),
                edgeMat
            );
            edges.position.copy(mesh.position);
            g.add(mesh, edges);
        };

        volume(2.6, 3.8, 2.6, 0, 0, 0); // main tower, top at 3.8
        volume(1.7, 2.6, 1.7, -2.9, 0, 0.8);
        volume(2.0, 1.1, 1.6, 2.9, 0, 1.2); // house
        const roofGeo = new THREE.ConeGeometry(1.5, 0.9, 4);
        const roof = new THREE.Mesh(roofGeo, faceMat);
        roof.position.set(2.9, 1.1 + 0.45, 1.2);
        roof.rotation.y = Math.PI / 4;
        g.add(roof);
        const roofEdges = new THREE.LineSegments(
            new THREE.EdgesGeometry(roofGeo),
            edgeMat
        );
        roofEdges.position.copy(roof.position);
        roofEdges.rotation.copy(roof.rotation);
        g.add(roofEdges);

        // lightning rod on main tower (tip at y=5.2)
        const rod = new THREE.Mesh(
            new THREE.CylinderGeometry(0.022, 0.035, 1.4, 8),
            new THREE.MeshBasicMaterial({ color: VOLT, toneMapped: false })
        );
        rod.position.set(0, 3.8 + 0.7, 0);
        g.add(rod);
        const tip = new THREE.Mesh(
            new THREE.SphereGeometry(0.07, 12, 12),
            new THREE.MeshBasicMaterial({ color: 0xffffff, toneMapped: false })
        );
        tip.position.set(0, 5.2, 0);
        g.add(tip);

        // down conductor along tower edge to ground
        const condPts = [
            new THREE.Vector3(0.06, 5.05, 0.06),
            new THREE.Vector3(1.27, 4.0, 1.27),
            new THREE.Vector3(1.3, 2.0, 1.3),
            new THREE.Vector3(1.3, 0.02, 1.3),
        ];
        const cond = new THREE.Line(
            new THREE.BufferGeometry().setFromPoints(
                new THREE.CatmullRomCurve3(condPts).getPoints(30)
            ),
            new THREE.LineBasicMaterial({
                color: VOLT,
                transparent: true,
                opacity: 0.4,
            })
        );
        g.add(cond);

        // ground
        const ground = new THREE.Mesh(
            new THREE.CircleGeometry(20, 48),
            new THREE.MeshBasicMaterial({ color: 0x0b101d })
        );
        ground.rotation.x = -Math.PI / 2;
        ground.position.y = -0.02;
        g.add(ground);
        const grid = new THREE.GridHelper(38, 38, 0x243352, 0x141c2c);
        grid.position.y = 0.01;
        g.add(grid);

        this.rodTip = new THREE.Vector3(0, 5.2, 0);
        this.cityGroup = g;
        this.scene.add(g);
    }

    buildDome() {
        const group = new THREE.Group();
        group.position.set(0, 0.4, 0);
        const R = 5.35;

        this.domeFillMat = new THREE.MeshBasicMaterial({
            color: VOLT,
            transparent: true,
            opacity: 0.045,
            side: THREE.DoubleSide,
            depthWrite: false,
        });
        const fill = new THREE.Mesh(
            new THREE.SphereGeometry(R, 40, 22, 0, Math.PI * 2, 0, Math.PI * 0.56),
            this.domeFillMat
        );
        this.domeWireMat = new THREE.MeshBasicMaterial({
            color: VOLT,
            wireframe: true,
            transparent: true,
            opacity: 0.13,
            depthWrite: false,
        });
        const wire = new THREE.Mesh(
            new THREE.IcosahedronGeometry(R, 2),
            this.domeWireMat
        );
        group.add(fill, wire);
        this.domeGroup = group;
        this.domeR = R;
        this.scene.add(group);
    }

    buildStars() {
        const n = 320;
        const pos = new Float32Array(n * 3);
        for (let i = 0; i < n; i++) {
            pos[i * 3] = (Math.random() - 0.5) * 70;
            pos[i * 3 + 1] = Math.random() * 30 + 4;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 70;
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
        const stars = new THREE.Points(
            geo,
            new THREE.PointsMaterial({
                color: 0x8ea6d8,
                size: 0.06,
                transparent: true,
                opacity: 0.55,
                sizeAttenuation: true,
                fog: false,
            })
        );
        this.scene.add(stars);
    }

    setShield(on) {
        this.shieldOn = on;
    }

    setKv(kv) {
        this.kv = kv;
    }

    strike() {
        const kv = this.kv;
        let target = this.rodTip.clone();
        if (this.shieldOn) {
            const c = new THREE.Vector3(0, 0.4, 0);
            const dir = new THREE.Vector3(
                (Math.random() - 0.5) * 0.5,
                1,
                (Math.random() - 0.5) * 0.5
            ).normalize();
            target = c.clone().add(dir.multiplyScalar(this.domeR + 0.08));
        }
        const start = new THREE.Vector3(
            (Math.random() - 0.5) * 4.5,
            14.5 + Math.random() * 2.5,
            (Math.random() - 0.5) * 4.5
        );
        const rough = 0.3 + (kv / 1000) * 0.28;
        const pts = jaggedPath(start, target, 5, rough);

        const bolt = new THREE.Group();
        const core = tubeAlong(pts, 0.035, 0xffffff, 1);
        const halo = tubeAlong(pts, 0.12, VOLT, 0.3);
        bolt.add(core.mesh, halo.mesh);
        const parts = [core, halo];

        const nBranches = 2 + Math.floor(kv / 380);
        for (let i = 0; i < nBranches; i++) {
            const p = pts[Math.floor(pts.length * (0.25 + 0.5 * Math.random()))];
            const end = p
                .clone()
                .add(
                    new THREE.Vector3(
                        (Math.random() - 0.5) * 3.2,
                        -(1 + Math.random() * 2.6),
                        (Math.random() - 0.5) * 3.2
                    )
                );
            const bp = jaggedPath(p, end, 3, 0.42);
            const bc = tubeAlong(bp, 0.016, 0xfff9c0, 0.75);
            const bh = tubeAlong(bp, 0.06, VOLT, 0.14);
            bolt.add(bc.mesh, bh.mesh);
            parts.push(bc, bh);
        }

        const sprite = new THREE.Sprite(
            new THREE.SpriteMaterial({
                map: this.glowTex,
                color: VOLT,
                transparent: true,
                blending: THREE.AdditiveBlending,
                depthWrite: false,
                toneMapped: false,
            })
        );
        sprite.position.copy(target);
        sprite.scale.setScalar(0.01);
        bolt.add(sprite);

        this.scene.add(bolt);
        this.bolts.push({ bolt, parts, sprite, t: 0 });
        if (this.bolts.length > 6) this._removeBolt(this.bolts[0]);

        this.flash.position.copy(target);
        this.flashI = 90 + kv * 0.22;

        this.onStrike({
            ka: Math.round(12 + kv / 25 + Math.random() * 6),
            pv: kv,
            shield: this.shieldOn,
        });
    }

    _removeBolt(b) {
        this.scene.remove(b.bolt);
        b.parts.forEach((p) => {
            p.geo.dispose();
            p.mat.dispose();
        });
        b.sprite.material.dispose();
        const i = this.bolts.indexOf(b);
        if (i >= 0) this.bolts.splice(i, 1);
    }

    resize(w, h) {
        if (!w || !h) return;
        this.camera.aspect = w / h;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(w, h, false);
    }

    loop() {
        if (this.disposed) return;
        this._raf = requestAnimationFrame(this._loop);
        const dt = Math.min(this.clock.getDelta(), 0.05);

        // flash decay
        this.flashI *= Math.exp(-7 * dt);
        this.flash.intensity = this.flashI;

        // dome lerp
        const target = this.shieldOn ? 1 : 0.0001;
        this.domeT += (target - this.domeT) * Math.min(1, dt * 6);
        this.domeGroup.scale.setScalar(Math.max(this.domeT, 0.0001));
        this.domeGroup.visible = this.domeT > 0.01;
        const f = this.domeT;
        this.domeFillMat.opacity = 0.045 * f;
        this.domeWireMat.opacity = 0.13 * f;

        // bolts lifecycle
        for (let i = this.bolts.length - 1; i >= 0; i--) {
            const b = this.bolts[i];
            b.t += dt;
            const grow = Math.min(1, b.t / 0.14);
            b.parts.forEach((p) =>
                p.geo.setDrawRange(0, Math.floor(p.total * grow))
            );
            let q = 1;
            if (b.t > 0.22) q = Math.max(0, 1 - (b.t - 0.22) / 0.8);
            b.parts.forEach((p) => (p.mat.opacity = p.mesh.userData.baseOpacity * q));
            const ss = (0.6 + b.t * 10) * q;
            b.sprite.scale.setScalar(Math.max(ss, 0.01));
            b.sprite.material.opacity = q;
            if (q <= 0) this._removeBolt(b);
        }

        // camera parallax
        this.pointer.x += (this.pointer.tx - this.pointer.x) * Math.min(1, dt * 3);
        this.pointer.y += (this.pointer.ty - this.pointer.y) * Math.min(1, dt * 3);
        this.camera.position.x = this.camBase.x + this.pointer.x * 1.1;
        this.camera.position.y = this.camBase.y + this.pointer.y * 0.55;
        this.camera.lookAt(0, 2.6, 0);

        // auto strikes
        if (this.autoEnabled && this.inView) {
            this.autoTimer -= dt;
            if (this.autoTimer <= 0) {
                this.strike();
                this.autoTimer = 4.5 + Math.random() * 3.5;
            }
        }

        this.renderer.render(this.scene, this.camera);
    }

    dispose() {
        this.disposed = true;
        cancelAnimationFrame(this._raf);
        this.canvas.parentElement.removeEventListener(
            "pointermove",
            this._onPointer
        );
        this.scene.traverse((o) => {
            if (o.geometry) o.geometry.dispose();
            if (o.material) {
                if (Array.isArray(o.material)) o.material.forEach((m) => m.dispose());
                else o.material.dispose();
            }
        });
        this.renderer.dispose();
    }
}
